import React from 'react';
import { GetCatalogProductsQuery, useGetCatalogProductsQuery, CatalogProductsFilterInput } from 'data/__generated__';
import { Option, TotemSelect } from 'components/TotemSelect';

export type ProductSelected = GetCatalogProductsQuery['catalogProducts']['products'][number];

interface ProductSelectProps {
    label?: string;
    placeholder?: string;
    dataTest?: string;
    value: string | null;
    onChange: (product: ProductSelected | null) => void;
    filters?: CatalogProductsFilterInput;
    selectedProduct?: {
        _id: string;
        name: string;
        brand?: string | null;
        volume?: string | null;
    } | null;
}

export const ProductSelect: React.FC<ProductSelectProps> = ({
    label = 'Choisir un seul produit',
    placeholder = 'Sélectionner un produit (recherche par nom ou par _id)',
    value,
    onChange,
    dataTest,
    filters,
    selectedProduct = null,
}) => {
    const { data: productsData, refetch } = useGetCatalogProductsQuery({
        variables: {
            offset: 0,
            limit: 30,
            sortBy: 'name',
            sortOrder: 'asc',
            filters: {
                ...filters,
            },
        },
        fetchPolicy: 'cache-and-network',
    });

    const buildProductLabel = (product: { name: string; brand?: string | null; volume?: string | null }) => {
        let label = product.name;
        if (product.brand) {
            label += ` - ${product.brand}`;
        }
        if (product.volume) {
            label += ` - ${product.volume}`;
        }
        return label;
    };

    const selectedProductOption = React.useMemo(
        () =>
            selectedProduct
                ? {
                      label: buildProductLabel(selectedProduct),
                      value: selectedProduct._id,
                  }
                : null,
        [selectedProduct],
    );

    const defaultOptions = React.useMemo(() => {
        const options = (productsData?.catalogProducts?.products || []).map((product) => ({
            label: buildProductLabel(product),
            value: product._id,
        }));

        if (selectedProductOption && !options.some((option) => option.value === selectedProductOption.value)) {
            return [selectedProductOption, ...options];
        }

        return options;
    }, [productsData, selectedProductOption]);

    const fetchOptions = React.useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const result = await refetch({
                offset: 0,
                limit: 30,
                sortBy: 'name',
                sortOrder: 'asc',
                filters: {
                    ...filters,
                    searchValue: query || undefined,
                },
            });

            return (result.data?.catalogProducts?.products || []).map((product) => ({
                label: buildProductLabel(product),
                value: product._id,
            }));
        },
        [refetch, filters],
    );

    const handleChange = (productId: string | null) => {
        const product = productsData?.catalogProducts?.products.find((p) => p._id === productId) ?? null;
        onChange(product);
    };

    return (
        <TotemSelect
            dataTest={dataTest}
            label={label}
            placeholder={placeholder}
            value={value}
            onChange={handleChange}
            fetchOptions={fetchOptions}
            defaultOptions={defaultOptions}
        />
    );
};
